import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { FlexRow } from '@editor/components/utilities/layouts';

import OTPResend from './OTPResend';
import Slot from './Slot';
import {
  ErrorText,
  HiddenInput,
  SeparatorWrapper,
  Title,
  Wrapper,
} from './styles';
import { OtpInputProps, SeparatorProps } from './types';

const Separator: React.FC<SeparatorProps> = ({ error }) => {
  return <SeparatorWrapper error={!!error}>-</SeparatorWrapper>;
};

const OtpInput: React.FC<OtpInputProps> = ({
  length,
  onChange,
  onComplete,
  value,
  split,
  error,
  title,
  timeToResend,
  onResend,
  disabled,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [focusedIndex, setFocusedIndex] = useState(0);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  useEffect(() => {
    if (value.length === length && onComplete) {
      onComplete();
    }
  }, [length, value, onComplete]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>): void => {
      if (disabled) {
        return;
      }

      const value = e.target.value;
      if (/^\d*$/.test(value) && value.length <= length) {
        onChange(value);
        setFocusedIndex(value.length);
      }
    },
    [length, onChange, disabled]
  );

  const handlePaste = useCallback(
    (e: React.ClipboardEvent<HTMLInputElement>): void => {
      e.preventDefault();
      if (disabled) {
        return;
      }
      const pasteData = e.clipboardData.getData('text');
      const numericData = pasteData.replace(/\D/g, '');
      if (numericData) {
        const updatedValue = value + numericData;
        const truncatedData = updatedValue.slice(0, length);
        onChange(truncatedData);
        setFocusedIndex(truncatedData.length);
      }
    },
    [onChange, value, length, disabled]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>): void => {
      if (e.key === 'ArrowLeft' && focusedIndex > 0) {
        setFocusedIndex(focusedIndex - 1);
      } else if (e.key === 'ArrowRight' && focusedIndex < value.length) {
        setFocusedIndex(focusedIndex + 1);
      } else if (e.key === 'Backspace') {
        e.preventDefault();
        if (disabled) {
          return;
        }
        if (value[focusedIndex]) {
          const newValue =
            value.slice(0, focusedIndex) + value.slice(focusedIndex + 1);
          onChange(newValue);
        } else if (focusedIndex !== 0) {
          const newValue =
            value.slice(0, focusedIndex - 1) + value.slice(focusedIndex);
          onChange(newValue);
          setFocusedIndex(focusedIndex - 1);
        }
      }
    },
    [value, onChange, focusedIndex, disabled]
  );

  const handleFocus = useCallback((): void => {
    setFocusedIndex(value.length);
  }, [value]);

  const handleBlur = useCallback((): void => {
    setFocusedIndex(-1);
  }, []);

  const slots = useMemo(() => Array.from({ length }), [length]);

  return (
    <Wrapper onClick={(): void => inputRef.current?.focus()}>
      <HiddenInput
        ref={inputRef}
        maxLength={length}
        type="text"
        value={value}
        onBlur={handleBlur}
        onChange={handleChange}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
      />
      {title && <Title>{title}</Title>}
      <FlexRow gap="8px" marginTop="5px">
        {slots.map((_, index) => {
          const isLastSlot = index === length - 1;
          const isSplitPlace = !!(split && (index + 1) % split === 0);
          return (
            <React.Fragment key={index}>
              <Slot
                error={error}
                isFocused={focusedIndex === index}
                value={value[index]}
              />
              {isSplitPlace && !isLastSlot && <Separator error={error} />}
            </React.Fragment>
          );
        })}
      </FlexRow>
      {!!error && <ErrorText>{error}</ErrorText>}
      <OTPResend timeToResend={timeToResend ?? 0} onResend={onResend} />
    </Wrapper>
  );
};

export default OtpInput;
